import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateOperationTagsApplicationErrors,
  BulkUpdateOperationTagsByFilterApplicationErrors,
  BulkUpdateOperationTagsByFilterOptions,
  BulkUpdateOperationTagsByFilterResponse,
  BulkUpdateOperationTagsOptions,
  BulkUpdateOperationTagsResponse,
  CalculateAvailableDatesInRangeOptions,
  CalculateAvailableDatesInRangeResponse,
  CalculateAvailableTimeSlotsForDateOptions,
  CalculateAvailableTimeSlotsForDateResponse,
  CalculateFirstAvailableTimeSlotPerFulfillmentTypeOptions,
  CalculateFirstAvailableTimeSlotPerFulfillmentTypeResponse,
  CalculateFirstAvailableTimeSlotsForItemOptions,
  CalculateFirstAvailableTimeSlotsForItemResponse,
  CalculateFirstAvailableTimeSlotsPerMenuOptions,
  CalculateFirstAvailableTimeSlotsPerMenuResponse,
  CalculateFirstAvailableTimeSlotsPerOperationOptions,
  CalculateFirstAvailableTimeSlotsPerOperationResponse,
  ListAvailableDatesInRangeOptions,
  ListAvailableDatesInRangeResponse,
  ListAvailableFulfillmentOptions,
  ListAvailableFulfillmentOptionsResponse,
  ListAvailableTimeSlotsForDateOptions,
  ListAvailableTimeSlotsForDateResponse,
  ListBlockedPeriodsOptions,
  ListBlockedPeriodsResponse,
  ListFirstAvailableTimeSlotForFulfillmentTypesOptions,
  ListFirstAvailableTimeSlotForFulfillmentTypesResponse,
  ListFirstAvailableTimeSlotsForMenusOptions,
  ListFirstAvailableTimeSlotsForMenusResponse,
  ListFirstAvailableTimeSlotsForOperationsOptions,
  ListFirstAvailableTimeSlotsForOperationsResponse,
  ListOperationsResponse,
  Operation,
  OperationDeletedEnvelope,
  OperationQuery,
  OperationUpdatedEnvelope,
  OperationsQueryBuilder,
  QueryOperationResponse,
  UpdateOperation,
  ValidateOperationAddressResponse,
  bulkUpdateOperationTags as universalBulkUpdateOperationTags,
  bulkUpdateOperationTagsByFilter as universalBulkUpdateOperationTagsByFilter,
  calculateAvailableDatesInRange as universalCalculateAvailableDatesInRange,
  calculateAvailableTimeSlotsForDate as universalCalculateAvailableTimeSlotsForDate,
  calculateFirstAvailableTimeSlotPerFulfillmentType as universalCalculateFirstAvailableTimeSlotPerFulfillmentType,
  calculateFirstAvailableTimeSlotsForItem as universalCalculateFirstAvailableTimeSlotsForItem,
  calculateFirstAvailableTimeSlotsPerMenu as universalCalculateFirstAvailableTimeSlotsPerMenu,
  calculateFirstAvailableTimeSlotsPerOperation as universalCalculateFirstAvailableTimeSlotsPerOperation,
  deleteOperation as universalDeleteOperation,
  getOperation as universalGetOperation,
  listAvailableDatesInRange as universalListAvailableDatesInRange,
  listAvailableFulfillmentOptions as universalListAvailableFulfillmentOptions,
  listAvailableTimeSlotsForDate as universalListAvailableTimeSlotsForDate,
  listBlockedPeriods as universalListBlockedPeriods,
  listFirstAvailableTimeSlotForFulfillmentTypes as universalListFirstAvailableTimeSlotForFulfillmentTypes,
  listFirstAvailableTimeSlotsForMenus as universalListFirstAvailableTimeSlotsForMenus,
  listFirstAvailableTimeSlotsForOperations as universalListFirstAvailableTimeSlotsForOperations,
  listOperations as universalListOperations,
  queryOperation as universalQueryOperation,
  typedQueryOperation as universalTypedQueryOperation,
  updateOperation as universalUpdateOperation,
  validateOperationAddress as universalValidateOperationAddress,
} from './restaurants-operations-v1-operation-operations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function listBlockedPeriods(
  httpClient: HttpClient
): ListBlockedPeriodsSignature {
  return (
    operationId: string,
    options?: NonNullablePaths<ListBlockedPeriodsOptions, `until`, 2>
  ) =>
    universalListBlockedPeriods(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListBlockedPeriodsSignature {
  /**
   * List Blocked Periods.
   * @param - ID of the restaurant operation this blocked period is associated with. (See the Restaurants Operations API for more information.)
   */
  (
    operationId: string,
    options?: NonNullablePaths<ListBlockedPeriodsOptions, `until`, 2>
  ): Promise<NonNullablePaths<ListBlockedPeriodsResponse, `blockPeriods`, 2>>;
}

export function getOperation(httpClient: HttpClient): GetOperationSignature {
  return (operationId: string) =>
    universalGetOperation(
      operationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOperationSignature {
  /**
   * Retrieves an operation.
   * @param - ID of the operation to retrieve.
   * @returns Retrieved operation.
   */
  (operationId: string): Promise<
    NonNullablePaths<
      Operation,
      | `fulfillmentIds`
      | `onlineOrderingStatus`
      | `defaultFulfillmentType`
      | `orderScheduling.asapOptions.preparationTime.maxTimeOptions.timeUnit`
      | `orderScheduling.asapOptions.preparationTime.timeRangeOptions.timeUnit`
      | `orderScheduling.asapOptions.preparationTime.type`
      | `orderScheduling.asapOptions.asapFutureHandlingType`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.dayOfWeek`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.timeOfDay.hours`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.timeOfDay.minutes`
      | `orderScheduling.preorderOptions.method.type`
      | `orderScheduling.preorderOptions.fulfillmentTimesDisplay.type`
      | `orderScheduling.type`
      | `tags.privateTags.tagIds`,
      8
    >
  >;
}

export function updateOperation(
  httpClient: HttpClient
): UpdateOperationSignature {
  return (
    _id: string,
    operation: NonNullablePaths<UpdateOperation, `revision`, 2>
  ) =>
    universalUpdateOperation(
      _id,
      operation,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOperationSignature {
  /**
   * Updates an operation.
   *
   * If you update part of the `orderScheduling` property, the whole object is overwritten,
   * so you must include the entire object unless you are not updating `orderScheduling` at all. <br />
   *
   * Each time the operation is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the operation.
   * This ensures you're working with the latest operation
   * and prevents unintended overwrites.
   * @param - Operation ID.
   * @returns Updated operation.
   */
  (
    _id: string,
    operation: NonNullablePaths<UpdateOperation, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Operation,
      | `fulfillmentIds`
      | `onlineOrderingStatus`
      | `defaultFulfillmentType`
      | `orderScheduling.asapOptions.preparationTime.maxTimeOptions.timeUnit`
      | `orderScheduling.asapOptions.preparationTime.timeRangeOptions.timeUnit`
      | `orderScheduling.asapOptions.preparationTime.type`
      | `orderScheduling.asapOptions.asapFutureHandlingType`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.dayOfWeek`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.timeOfDay.hours`
      | `orderScheduling.preorderOptions.method.weeklyScheduleOptions.cutOffTime.timeOfDay.minutes`
      | `orderScheduling.preorderOptions.method.type`
      | `orderScheduling.preorderOptions.fulfillmentTimesDisplay.type`
      | `orderScheduling.type`
      | `tags.privateTags.tagIds`,
      8
    >
  >;
}

export function deleteOperation(
  httpClient: HttpClient
): DeleteOperationSignature {
  return (operationId: string) =>
    universalDeleteOperation(
      operationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteOperationSignature {
  /**
   * Deletes an operation.
   * @param - ID of the operation to delete.
   * @deprecated
   */
  (operationId: string): Promise<void>;
}

export function queryOperation(
  httpClient: HttpClient
): QueryOperationSignature {
  return () =>
    universalQueryOperation(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryOperationSignature {
  /**
   * Creates a query to retrieve a list of operations.
   *
   * The `queryOperations()` function builds a query to retrieve a list of operations and returns an `OperationsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/operations/operations-query-builder/find) function.
   *
   * You can refine the query by chaining `OperationsQueryBuilder` functions onto the query. `OperationsQueryBuilder` functions enable you to filter, sort, and control the results that `queryOperations()` returns.
   *
   * `queryOperations()` runs with the following `OperationsQueryBuilder` defaults, which you can override:
   *
   * * [`limit(50)`](/operations/operations-query-builder/limit)
   * * [`ascending('entityId')`](/operations/operations-methods-query-builder/ascending)
   *
   * The following `OperationsQueryBuilder` functions are supported for `queryOperations()`. For a full description of the operations object, see the object returned for the [`items`](/operations/operations-query-result/items) property in `OperationsQueryResult`.
   */
  (): OperationsQueryBuilder;
}

export function typedQueryOperation(
  httpClient: HttpClient
): TypedQueryOperationSignature {
  return (query: OperationQuery) =>
    universalTypedQueryOperation(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryOperationSignature {
  /** */
  (query: OperationQuery): Promise<
    NonNullablePaths<
      QueryOperationResponse,
      | `operations`
      | `operations.${number}.onlineOrderingStatus`
      | `operations.${number}.defaultFulfillmentType`
      | `operations.${number}.orderScheduling.asapOptions.asapFutureHandlingType`
      | `operations.${number}.orderScheduling.type`,
      6
    >
  >;
}

export function listOperations(
  httpClient: HttpClient
): ListOperationsSignature {
  return () =>
    universalListOperations(
      // @ts-ignore
      { httpClient }
    );
}

interface ListOperationsSignature {
  /**
   * Retrieves a list of operations.
   * The result will be sorted by created date in ascending order.
   */
  (): Promise<
    NonNullablePaths<
      ListOperationsResponse,
      | `operations`
      | `operations.${number}.onlineOrderingStatus`
      | `operations.${number}.defaultFulfillmentType`
      | `operations.${number}.orderScheduling.asapOptions.asapFutureHandlingType`
      | `operations.${number}.orderScheduling.type`,
      6
    >
  >;
}

export function listAvailableFulfillmentOptions(
  httpClient: HttpClient
): ListAvailableFulfillmentOptionsSignature {
  return (operationId: string, options?: ListAvailableFulfillmentOptions) =>
    universalListAvailableFulfillmentOptions(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableFulfillmentOptionsSignature {
  /**
   * Retrieves a list of available fulfillment options.
   *
   * What makes a fulfillment option available is whether you can submit an order given the scheduling configurations and the fulfillment method's availability.
   * When a delivery address is not provided in the input, our system retrieves a list encompassing all types of fulfillment methods.
   * Conversely, if a delivery address` is given, the response may includes non-delivery fulfillment options along with delivery fulfillment methods that are applicable to the given address, ensuring the address falls within the defined delivery area of these methods.
   * @param - Operation ID. Returned fulfillment options will belong to this operation.
   */
  (operationId: string, options?: ListAvailableFulfillmentOptions): Promise<
    NonNullablePaths<
      ListAvailableFulfillmentOptionsResponse,
      | `pickupConfigured`
      | `deliveryConfigured`
      | `fulfillmentOptions`
      | `fulfillmentOptions.${number}.maxTimeOptions`
      | `fulfillmentOptions.${number}.durationRangeOptions.minDuration`
      | `fulfillmentOptions.${number}.durationRangeOptions.maxDuration`
      | `fulfillmentOptions.${number}.timeWindowsOptions.durationInMinutes`
      | `fulfillmentOptions.${number}.type`
      | `fulfillmentOptions.${number}.availability.asapHandlingAvailable`
      | `fulfillmentOptions.${number}.fulfillmentTimeType`
      | `fulfillmentOptions.${number}.fulfillmentTimesDisplayType`,
      5
    >
  >;
}

export function listFirstAvailableTimeSlotForFulfillmentTypes(
  httpClient: HttpClient
): ListFirstAvailableTimeSlotForFulfillmentTypesSignature {
  return (
    operationId: string,
    options?: ListFirstAvailableTimeSlotForFulfillmentTypesOptions
  ) =>
    universalListFirstAvailableTimeSlotForFulfillmentTypes(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFirstAvailableTimeSlotForFulfillmentTypesSignature {
  /**
   * Retrieves a list of available time slots for each fulfillment type.
   *
   * Each time slot is the first available time slot for the given fulfillment type.
   * @param - Operation ID.
   * Returned fulfillment options will belong to this operation.
   * @deprecated
   */
  (
    operationId: string,
    options?: ListFirstAvailableTimeSlotForFulfillmentTypesOptions
  ): Promise<
    NonNullablePaths<
      ListFirstAvailableTimeSlotForFulfillmentTypesResponse,
      | `timeSlots`
      | `timeSlots.${number}.fulfilmentType`
      | `timeSlots.${number}.startsNow`,
      4
    >
  >;
}

export function calculateFirstAvailableTimeSlotPerFulfillmentType(
  httpClient: HttpClient
): CalculateFirstAvailableTimeSlotPerFulfillmentTypeSignature {
  return (
    operationId: string,
    options?: CalculateFirstAvailableTimeSlotPerFulfillmentTypeOptions
  ) =>
    universalCalculateFirstAvailableTimeSlotPerFulfillmentType(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateFirstAvailableTimeSlotPerFulfillmentTypeSignature {
  /**
   * Retrieves the first available time slot of each fulfillment type for a specified operation.
   *
   * The response includes a time slot with the delivery fulfillment type only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Operation ID.
   */
  (
    operationId: string,
    options?: CalculateFirstAvailableTimeSlotPerFulfillmentTypeOptions
  ): Promise<
    NonNullablePaths<
      CalculateFirstAvailableTimeSlotPerFulfillmentTypeResponse,
      | `timeslotsPerFulfillmentType`
      | `timeslotsPerFulfillmentType.${number}.timeSlot.orderSchedulingType`
      | `timeslotsPerFulfillmentType.${number}.fulfilmentType`,
      5
    >
  >;
}

export function listFirstAvailableTimeSlotsForOperations(
  httpClient: HttpClient
): ListFirstAvailableTimeSlotsForOperationsSignature {
  return (
    operationIds: string[],
    options?: ListFirstAvailableTimeSlotsForOperationsOptions
  ) =>
    universalListFirstAvailableTimeSlotsForOperations(
      operationIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFirstAvailableTimeSlotsForOperationsSignature {
  /**
   * Retrieves a list of available time slots for each fulfillment type.
   *
   * Each time slot is the first available time slot for the given fulfillment type.
   * @param - Operation ID.
   * Returned fulfillment options will belong to this operation.
   * @deprecated
   */
  (
    operationIds: string[],
    options?: ListFirstAvailableTimeSlotsForOperationsOptions
  ): Promise<
    NonNullablePaths<
      ListFirstAvailableTimeSlotsForOperationsResponse,
      `timeSlots` | `timeSlots.${number}.operationId`,
      4
    >
  >;
}

export function calculateFirstAvailableTimeSlotsPerOperation(
  httpClient: HttpClient
): CalculateFirstAvailableTimeSlotsPerOperationSignature {
  return (
    operationIds: string[],
    options?: CalculateFirstAvailableTimeSlotsPerOperationOptions
  ) =>
    universalCalculateFirstAvailableTimeSlotsPerOperation(
      operationIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateFirstAvailableTimeSlotsPerOperationSignature {
  /**
   * Retrieves the first available time slot of each fulfillment type for each specified operation.
   *
   * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Operation IDs.
   */
  (
    operationIds: string[],
    options?: CalculateFirstAvailableTimeSlotsPerOperationOptions
  ): Promise<
    NonNullablePaths<
      CalculateFirstAvailableTimeSlotsPerOperationResponse,
      `timeSlotsPerOperation` | `timeSlotsPerOperation.${number}.operationId`,
      4
    >
  >;
}

export function listFirstAvailableTimeSlotsForMenus(
  httpClient: HttpClient
): ListFirstAvailableTimeSlotsForMenusSignature {
  return (
    operationId: string,
    options?: ListFirstAvailableTimeSlotsForMenusOptions
  ) =>
    universalListFirstAvailableTimeSlotsForMenus(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFirstAvailableTimeSlotsForMenusSignature {
  /**
   * For each menu, retrieves the first available time slots for each fulfillment type.
   * @param - Operation ID.
   * Returned timeslots that are belong to this operation.
   * @deprecated
   */
  (
    operationId: string,
    options?: ListFirstAvailableTimeSlotsForMenusOptions
  ): Promise<
    NonNullablePaths<
      ListFirstAvailableTimeSlotsForMenusResponse,
      `timeSlotsPerMenu`,
      2
    >
  >;
}

export function calculateFirstAvailableTimeSlotsPerMenu(
  httpClient: HttpClient
): CalculateFirstAvailableTimeSlotsPerMenuSignature {
  return (
    operationId: string,
    options?: CalculateFirstAvailableTimeSlotsPerMenuOptions
  ) =>
    universalCalculateFirstAvailableTimeSlotsPerMenu(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateFirstAvailableTimeSlotsPerMenuSignature {
  /**
   * Retrieves the first available time slot of each fulfillment type for each specified menu.
   *
   * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Operation ID.
   */
  (
    operationId: string,
    options?: CalculateFirstAvailableTimeSlotsPerMenuOptions
  ): Promise<
    NonNullablePaths<
      CalculateFirstAvailableTimeSlotsPerMenuResponse,
      `timeSlotsPerMenu`,
      2
    >
  >;
}

export function calculateFirstAvailableTimeSlotsForItem(
  httpClient: HttpClient
): CalculateFirstAvailableTimeSlotsForItemSignature {
  return (
    operationId: string,
    menuId: string,
    options?: NonNullablePaths<
      CalculateFirstAvailableTimeSlotsForItemOptions,
      `itemId` | `sectionId`,
      2
    >
  ) =>
    universalCalculateFirstAvailableTimeSlotsForItem(
      operationId,
      menuId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateFirstAvailableTimeSlotsForItemSignature {
  /**
   * Retrieves the first available time slot of each fulfillment type for a spastic Item.
   *
   * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Menu ID to get the first available time slots for.
   */
  (
    operationId: string,
    menuId: string,
    options?: NonNullablePaths<
      CalculateFirstAvailableTimeSlotsForItemOptions,
      `itemId` | `sectionId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      CalculateFirstAvailableTimeSlotsForItemResponse,
      | `timeslotsPerFulfillmentType`
      | `timeslotsPerFulfillmentType.${number}.timeSlot.orderSchedulingType`
      | `timeslotsPerFulfillmentType.${number}.fulfilmentType`,
      5
    >
  >;
}

export function listAvailableTimeSlotsForDate(
  httpClient: HttpClient
): ListAvailableTimeSlotsForDateSignature {
  return (
    operationId: string,
    options?: NonNullablePaths<
      ListAvailableTimeSlotsForDateOptions,
      `date` | `date.day` | `date.month` | `date.year`,
      3
    >
  ) =>
    universalListAvailableTimeSlotsForDate(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableTimeSlotsForDateSignature {
  /**
   * Retrieves a list of the available time slots for a given date.
   * @param - Operation ID.
   * The returned fulfillment options will belong to this operation.
   * @deprecated
   */
  (
    operationId: string,
    options?: NonNullablePaths<
      ListAvailableTimeSlotsForDateOptions,
      `date` | `date.day` | `date.month` | `date.year`,
      3
    >
  ): Promise<
    NonNullablePaths<
      ListAvailableTimeSlotsForDateResponse,
      | `timeSlots`
      | `timeSlots.${number}.fulfilmentType`
      | `timeSlots.${number}.startsNow`,
      4
    >
  >;
}

export function calculateAvailableTimeSlotsForDate(
  httpClient: HttpClient
): CalculateAvailableTimeSlotsForDateSignature {
  return (
    operationId: string,
    options?: NonNullablePaths<
      CalculateAvailableTimeSlotsForDateOptions,
      `date` | `date.day` | `date.month` | `date.year`,
      3
    >
  ) =>
    universalCalculateAvailableTimeSlotsForDate(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateAvailableTimeSlotsForDateSignature {
  /**
   * Retrieves all available time slots of each fulfillment type for the specified date.
   *
   * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Operation ID.
   */
  (
    operationId: string,
    options?: NonNullablePaths<
      CalculateAvailableTimeSlotsForDateOptions,
      `date` | `date.day` | `date.month` | `date.year`,
      3
    >
  ): Promise<
    NonNullablePaths<
      CalculateAvailableTimeSlotsForDateResponse,
      | `timeslotsPerFulfillmentType`
      | `timeslotsPerFulfillmentType.${number}.timeSlot.orderSchedulingType`
      | `timeslotsPerFulfillmentType.${number}.fulfilmentType`,
      5
    >
  >;
}

export function listAvailableDatesInRange(
  httpClient: HttpClient
): ListAvailableDatesInRangeSignature {
  return (
    operationId: string,
    options?: NonNullablePaths<
      ListAvailableDatesInRangeOptions,
      `from` | `until`,
      2
    >
  ) =>
    universalListAvailableDatesInRange(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableDatesInRangeSignature {
  /**
   * Retrieves a list of the available dates in a given time range.
   *
   * A date is considered available if it has at least one available time slot.
   * @param - Operation ID.
   * The returned fulfillment options will belong to this operation.
   * @deprecated
   */
  (
    operationId: string,
    options?: NonNullablePaths<
      ListAvailableDatesInRangeOptions,
      `from` | `until`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ListAvailableDatesInRangeResponse,
      `availableDates` | `availableDates.${number}.fulfilmentType`,
      4
    >
  >;
}

export function calculateAvailableDatesInRange(
  httpClient: HttpClient
): CalculateAvailableDatesInRangeSignature {
  return (
    operationId: string,
    options?: NonNullablePaths<
      CalculateAvailableDatesInRangeOptions,
      `from` | `until`,
      2
    >
  ) =>
    universalCalculateAvailableDatesInRange(
      operationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateAvailableDatesInRangeSignature {
  /**
   * Retrieves all available dates of each fulfillment type within the specified time range.
   *
   * A date is considered available if it has at least one available time slot.
   *
   * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
   * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
   * @param - Operation ID.
   */
  (
    operationId: string,
    options?: NonNullablePaths<
      CalculateAvailableDatesInRangeOptions,
      `from` | `until`,
      2
    >
  ): Promise<
    NonNullablePaths<
      CalculateAvailableDatesInRangeResponse,
      | `availableDatesPerFulfillmentType`
      | `availableDatesPerFulfillmentType.${number}.fulfilmentType`,
      4
    >
  >;
}

export function validateOperationAddress(
  httpClient: HttpClient
): ValidateOperationAddressSignature {
  return (operationId: string) =>
    universalValidateOperationAddress(
      operationId,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateOperationAddressSignature {
  /**
   * Validates an operation's address.
   *
   * The method checks if the operation's address stored in `businessLocationDetails.address` is valid.
   *
   * An address is considered valid if it has:
   *
   * 1. A non-empty, formatted address.
   * 2. Valid geocode coordinates (not 0,0).
   * 3. A non-empty country field.
   *
   * If the address is invalid, the response includes specific violations.
   * @param - The ID of the operation whose address should be validated.
   * @returns Response message for address validation
   */
  (operationId: string): Promise<
    NonNullablePaths<
      ValidateOperationAddressResponse,
      `valid` | `violations` | `violations.${number}.type`,
      4
    >
  >;
}

export function bulkUpdateOperationTags(
  httpClient: HttpClient
): BulkUpdateOperationTagsSignature {
  return (operationIds: string[], options?: BulkUpdateOperationTagsOptions) =>
    universalBulkUpdateOperationTags(
      operationIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOperationTagsSignature {
  /**
   * Synchronously update tags on multiple operations.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the operations to update tags for.
   */
  (operationIds: string[], options?: BulkUpdateOperationTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateOperationTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateOperationTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateOperationTagsByFilter(
  httpClient: HttpClient
): BulkUpdateOperationTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateOperationTagsByFilterOptions
  ) =>
    universalBulkUpdateOperationTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOperationTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple operations according to the specified filter.
   * If a filter isn't specified, this method updates all operations.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which operations to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateOperationTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateOperationTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateOperationTagsByFilterApplicationErrors;
    }
  >;
}

export const onOperationDeleted = EventDefinition(
  'wix.restaurants.operations.v1.operation_deleted',
  true,
  (event: OperationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.pausedUntilOptions.time' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.businessLocationDetails.address' }],
        },
      ])
    )
)<OperationDeletedEnvelope>();
export const onOperationUpdated = EventDefinition(
  'wix.restaurants.operations.v1.operation_updated',
  true,
  (event: OperationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.pausedUntilOptions.time' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.pausedUntilOptions.time' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.businessLocationDetails.address' },
            { path: 'modifiedFields.businessLocationDetails.address' },
          ],
        },
      ])
    )
)<OperationUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActivationType,
  ActivePeriod,
  Address,
  AddressHint,
  AddressLocation,
  AddressViolation,
  App,
  ApplicationError,
  AsapFutureHandlingType,
  AsapOrderPacing,
  AsapOrderPacingPacingAvailabilityOneOf,
  AsapOrderScheduling,
  AsapOrderSchedulingAsapFutureHandlingOptionsOneOf,
  AsapPacingWindowType,
  AsapPreorderType,
  AsapScheduling,
  AsapSchedulingAsapPreorderOneOf,
  AsapSchedulingPreparationTimeOneOf,
  Asset,
  AvailabilityException,
  BaseEventMetadata,
  BlockedPeriod,
  BulkActionMetadata,
  BulkUpdateOperationTagsByFilterOptions,
  BulkUpdateOperationTagsByFilterRequest,
  BulkUpdateOperationTagsByFilterResponse,
  BulkUpdateOperationTagsOptions,
  BulkUpdateOperationTagsRequest,
  BulkUpdateOperationTagsResponse,
  BulkUpdateOperationTagsResult,
  BusinessDaysAheadHandling,
  BusinessDaysPreorder,
  BusinessLocationDetails,
  BusinessSchedule,
  CalculateAvailableDatesInRangeOptions,
  CalculateAvailableDatesInRangeRequest,
  CalculateAvailableDatesInRangeResponse,
  CalculateAvailableTimeSlotsForDateOptions,
  CalculateAvailableTimeSlotsForDateRequest,
  CalculateAvailableTimeSlotsForDateResponse,
  CalculateFirstAvailableTimeSlotPerFulfillmentTypeOptions,
  CalculateFirstAvailableTimeSlotPerFulfillmentTypeRequest,
  CalculateFirstAvailableTimeSlotPerFulfillmentTypeResponse,
  CalculateFirstAvailableTimeSlotsForItemOptions,
  CalculateFirstAvailableTimeSlotsForItemRequest,
  CalculateFirstAvailableTimeSlotsForItemResponse,
  CalculateFirstAvailableTimeSlotsPerMenuOptions,
  CalculateFirstAvailableTimeSlotsPerMenuRequest,
  CalculateFirstAvailableTimeSlotsPerMenuResponse,
  CalculateFirstAvailableTimeSlotsPerOperationOptions,
  CalculateFirstAvailableTimeSlotsPerOperationRequest,
  CalculateFirstAvailableTimeSlotsPerOperationResponse,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CommonAddress,
  CommonAddressStreetOneOf,
  CommonCursorPaging,
  CommonCursorPagingMetadata,
  CommonCursors,
  ConsentPolicy,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DayAndTime,
  DayOfWeek,
  DayOfWeekAvailability,
  DeleteContext,
  DeleteOperationRequest,
  DeleteOperationResponse,
  DeleteStatus,
  DeliveryDetails,
  DeliveryProfileConfiguredForOperation,
  DineInDetails,
  DomainEvent,
  DomainEventBodyOneOf,
  DurationRange,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntitiesDayOfWeek,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  File,
  FirstFulfillmentTimeSlotsPerMenu,
  FulfillmentAddress,
  FulfillmentDetails,
  FulfillmentDetailsFulfillmentTimeOptionsOneOf,
  FulfillmentInfo,
  FulfillmentInfoFulfillmentTimeOneOf,
  FulfillmentOption,
  FulfillmentOptionAvailability,
  FulfillmentOptionFulfillmentTimeOptionsOneOf,
  FulfillmentOptionFulfillmentTimesDisplayOptionsOneOf,
  FulfillmentOptionFulfillmentTypeOptionsOneOf,
  FulfillmentTimeSlot,
  FulfillmentTimeSlotsPerMenu,
  FulfillmentTimeType,
  FulfillmentTimesDisplayConfig,
  FulfillmentTimesDisplayConfigFulfillmentTimesDisplayOptionsOneOf,
  FulfillmentTimesDisplayType,
  FulfillmentTimesType,
  FulfillmentType,
  FulfillmentTypeAvailableDates,
  GeoCoordinates,
  GetExpectedFulfillmentSelectionRequest,
  GetExpectedFulfillmentSelectionResponse,
  GetOperationRequest,
  GetOperationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImmutableFields,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ListAvailableDatesInRangeOptions,
  ListAvailableDatesInRangeRequest,
  ListAvailableDatesInRangeResponse,
  ListAvailableFulfillmentOptions,
  ListAvailableFulfillmentOptionsRequest,
  ListAvailableFulfillmentOptionsResponse,
  ListAvailableTimeSlotsForDateOptions,
  ListAvailableTimeSlotsForDateRequest,
  ListAvailableTimeSlotsForDateResponse,
  ListBlockedPeriodsOptions,
  ListBlockedPeriodsRequest,
  ListBlockedPeriodsResponse,
  ListFirstAvailableTimeSlotForFulfillmentTypesOptions,
  ListFirstAvailableTimeSlotForFulfillmentTypesRequest,
  ListFirstAvailableTimeSlotForFulfillmentTypesResponse,
  ListFirstAvailableTimeSlotsForMenusOptions,
  ListFirstAvailableTimeSlotsForMenusRequest,
  ListFirstAvailableTimeSlotsForMenusResponse,
  ListFirstAvailableTimeSlotsForOperationsOptions,
  ListFirstAvailableTimeSlotsForOperationsRequest,
  ListFirstAvailableTimeSlotsForOperationsResponse,
  ListOperationIdsRequest,
  ListOperationIdsResponse,
  ListOperationsInternalRequest,
  ListOperationsInternalResponse,
  ListOperationsRequest,
  ListOperationsResponse,
  ListOrdersForOperationRequest,
  ListOrdersForOperationResponse,
  Locale,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  MethodType,
  Multilingual,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  OnlineOrderingPausedUntilOptions,
  OnlineOrderingStatusType,
  Operation,
  OperationDeletedEnvelope,
  OperationOnlineOrderingStatusOptionsOneOf,
  OperationQuerySpec,
  OperationTimeSlot,
  OperationUpdatedEnvelope,
  OperationsDataCloningCompleted,
  OperationsQueryBuilder,
  OperationsQueryResult,
  OrderScheduling,
  OrderSchedulingOrderSchedulingOptionsOneOf,
  OrderSchedulingType,
  PacingOrder,
  Page,
  Pages,
  PagingMetadata,
  PicassoAssigned,
  PicassoUnassigned,
  PickupDetails,
  PlacementType,
  PreOrderPacing,
  PreOrderPacingPacingAvailabilityOneOf,
  PreorderMethod,
  PreorderMethodMethodOptionsOneOf,
  PreorderPacingWindowType,
  PreorderScheduling,
  PreparationTime,
  PreparationTimePreparationTimeType,
  PreparationTimeTimeSpecificationOneOf,
  PreparationTimeType,
  Properties,
  PropertiesChange,
  QueryOperationRequest,
  QueryOperationResponse,
  ResolutionMethod,
  RestoreInfo,
  Scheduling,
  SchedulingSchedulingOptionsOneOf,
  SchedulingType,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCloned,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  State,
  StreetAddress,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Subdivision,
  SubdivisionType,
  SupportedLanguage,
  TagList,
  Tags,
  TimeBounded,
  TimeDuration,
  TimeDurationRange,
  TimeOfDay,
  TimeOfDayRange,
  TimePeriod,
  TimeSlot,
  TimeSlotForFulfillment,
  TimeSlotForFulfillmentTimeSlotStatus,
  TimeSlotForOperation,
  TimeSlotStatus,
  TimeUnit,
  TimeWindowDisplayConfig,
  Translation,
  URI,
  URIs,
  UpdateDeliveryRegionsRequest,
  UpdateDeliveryRegionsResponse,
  UpdateImmutableFieldsRequest,
  UpdateImmutableFieldsResponse,
  UpdateOperation,
  UpdateOperationRequest,
  UpdateOperationResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  V1Address,
  V1AddressLocation,
  V1StreetAddress,
  V4SiteCreated,
  ValidateOperationAddressRequest,
  ValidateOperationAddressResponse,
  ViolationType,
  WebhookIdentityType,
  WeeklyActiveDays,
  WeeklyActivePeriods,
  WeeklySchedule,
  WixelAssigned,
  WixelUnassigned,
  _Date,
  utils,
} from './restaurants-operations-v1-operation-operations.universal.js';
